import type {
  WhatsAppAgentSummary,
  WhatsAppApprovedTemplate,
  WhatsAppCompanyGroup,
  WhatsAppContact,
  WhatsAppConversation,
  WhatsAppConversationStatus,
  WhatsAppDeliveryStatus,
  WhatsAppDirectoryContact,
  WhatsAppIntakeOption,
  WhatsAppMessage,
  WhatsAppMessageDirection,
  WhatsAppTraceEvent,
} from '../types/whatsapp';

/* ---- DTOs tal como los devuelve /api/whatsapp ---- */

export interface WhatsAppContactDto {
  id: number;
  phoneDisplay: string;
  phoneDigits: string;
  displayName?: string | null;
  email?: string | null;
  accountId?: number | null;
  accountName?: string | null;
  active: boolean;
}

export interface WhatsAppConversationSummaryDto {
  id: number;
  status: WhatsAppConversationStatus;
  origin?: string | null;
  lastMessagePreview?: string | null;
  lastMessageAt?: string | null;
  lastInboundAt?: string | null;
  windowHoursRemaining?: number | null;
  assignedAgentId?: number | null;
  assignedAgentName?: string | null;
  phoneCallId?: number | null;
  intakeLabel?: string | null;
  intakeAccountId?: number | null;
  intakeAccountName?: string | null;
  contact: WhatsAppContactDto;
  awaitingContactReply: boolean;
  createdAt?: string | null;
  closedAt?: string | null;
}

export interface WhatsAppMessageDto {
  id: number;
  direction: WhatsAppMessageDirection;
  body: string;
  sentAt: string;
  senderName?: string | null;
  templateCode?: string | null;
  deliveryStatus?: WhatsAppDeliveryStatus | null;
  errorDetail?: string | null;
}

export interface WhatsAppEventDto {
  id: number;
  eventAt: string;
  label: string;
  detail?: string | null;
  actorName?: string | null;
}

export interface WhatsAppConversationDetailDto {
  conversation: WhatsAppConversationSummaryDto;
  internalNotes?: string | null;
  messages: WhatsAppMessageDto[];
  events: WhatsAppEventDto[];
}

export interface WhatsAppTemplateDto {
  id: number;
  displayName: string;
  metaName: string;
  language: string;
  category?: string | null;
  body: string;
}

export interface WhatsAppAgentDto {
  id: number;
  fullName: string;
  online: boolean;
  openConversations: number;
}

export interface WhatsAppCompanyDto {
  id: number;
  name: string;
}

export interface WhatsAppIntakeOptionDto {
  id: number;
  label: string;
  companyId?: number | null;
  companyName?: string | null;
  employeeName?: string | null;
  phoneHint?: string | null;
}

export interface SpringPage<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

/* ---- Conversión a los tipos que usa la pantalla ---- */

const str = (v: number | null | undefined): string | null => (v == null ? null : String(v));

const ORIGIN_LABEL: Record<string, string> = {
  WA_ME_LINK: 'Contacto entrante',
  AGENT_OUTBOUND: 'Agente saliente',
};

const minutesSince = (iso?: string | null): number | undefined => {
  if (!iso) return undefined;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return undefined;
  return Math.max(0, Math.floor((Date.now() - t) / 60_000));
};

export const toDirectoryContact = (c: WhatsAppContactDto): WhatsAppDirectoryContact => ({
  id: String(c.id),
  phone: c.phoneDisplay,
  displayName: c.displayName ?? undefined,
  email: c.email ?? undefined,
  companyId: str(c.accountId),
  companyName: c.accountName ?? null,
  active: c.active,
});

const toConversationContact = (c: WhatsAppContactDto, hasIntake: boolean): WhatsAppContact => ({
  contactId: String(c.id),
  phone: c.phoneDisplay,
  displayName: c.displayName ?? undefined,
  email: c.email ?? undefined,
  identified: c.accountId != null || hasIntake,
  companyId: str(c.accountId),
  companyName: c.accountName ?? null,
});

export const toConversation = (s: WhatsAppConversationSummaryDto): WhatsAppConversation => {
  const waiting =
    s.status === 'NEW' || s.status === 'WAITING' ? minutesSince(s.lastMessageAt ?? s.createdAt) : undefined;
  return {
    id: String(s.id),
    contact: toConversationContact(s.contact, s.phoneCallId != null),
    status: s.status,
    assignedAgentId: str(s.assignedAgentId),
    assignedAgentName: s.assignedAgentName ?? null,
    lastMessagePreview: s.lastMessagePreview ?? '',
    lastMessageAt: s.lastMessageAt ?? s.createdAt ?? '',
    waitingMinutes: waiting,
    windowHoursRemaining: s.windowHoursRemaining ?? null,
    origin: s.origin ? ORIGIN_LABEL[s.origin] ?? s.origin : undefined,
    channel: 'WhatsApp',
    intakeId: str(s.phoneCallId),
    intakeLabel: s.intakeLabel ?? null,
    intakeCompanyId: str(s.intakeAccountId),
    intakeCompanyName: s.intakeAccountName ?? null,
    awaitingContactReply: s.awaitingContactReply,
    detailLoaded: false,
    messages: [],
    trace: [],
  };
};

const toMessage = (conversationId: string, m: WhatsAppMessageDto): WhatsAppMessage => ({
  id: String(m.id),
  conversationId,
  direction: m.direction,
  body: m.body,
  sentAt: m.sentAt,
  senderName: m.senderName ?? undefined,
  templateCode: m.templateCode ?? null,
  deliveryStatus: m.deliveryStatus ?? null,
  errorDetail: m.errorDetail ?? null,
});

const toTrace = (e: WhatsAppEventDto): WhatsAppTraceEvent => ({
  id: String(e.id),
  at: e.eventAt,
  label: e.label,
  detail: [e.detail, e.actorName ? `por ${e.actorName}` : null].filter(Boolean).join(' · ') || undefined,
});

export const toConversationDetail = (d: WhatsAppConversationDetailDto): WhatsAppConversation => {
  const base = toConversation(d.conversation);
  return {
    ...base,
    notes: d.internalNotes ?? '',
    detailLoaded: true,
    messages: d.messages.map((m) => toMessage(base.id, m)),
    // El backend los entrega del más reciente al más antiguo; la pantalla espera orden cronológico.
    trace: [...d.events].reverse().map(toTrace),
  };
};

export const toTemplate = (t: WhatsAppTemplateDto): WhatsAppApprovedTemplate => ({
  id: String(t.id),
  name: t.displayName,
  language: t.language,
  category: t.category ?? 'UTILITY',
  body: t.body,
  status: 'APPROVED',
});

export const toAgent = (a: WhatsAppAgentDto): WhatsAppAgentSummary => ({
  id: String(a.id),
  fullName: a.fullName,
  online: a.online,
  openConversations: a.openConversations,
});

export const toCompany = (c: WhatsAppCompanyDto): WhatsAppCompanyGroup => ({
  id: String(c.id),
  name: c.name,
});

export const toIntakeOption = (i: WhatsAppIntakeOptionDto): WhatsAppIntakeOption => ({
  id: String(i.id),
  label: i.label,
  companyId: str(i.companyId) ?? '',
  companyName: i.companyName ?? '',
  employeeName: i.employeeName ?? '',
  phoneHint: i.phoneHint ?? undefined,
});
